'use client';

import { useEffect } from 'react';

import * as Sentry from '@sentry/nextjs';

import ErrorScreen from 'components/shared/layout/ErrorScreen';

/**
 * The App Router's automatic error boundary, for exceptions thrown while rendering an
 * already-matched route. Carries `pages/_error.tsx`'s copy, image and
 * `Sentry.captureException` (#489, FR-010).
 *
 * Distinct from `app/error/page.tsx`, the routable `/error` page that `proxy.tsx`
 * rewrites to — a boundary is not addressable by URL, so both are needed.
 */
const Error = ({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) => {
  useEffect(() => {
    // Mirrors the reporting in pages/_error.tsx. No Sentry.flush() here: this
    // boundary only ever renders in the browser, where the transport is not
    // torn down the way a serverless invocation is.
    Sentry.captureException(error);
  }, [error]);

  return (
    <ErrorScreen description="An error occurred while loading this page.">
      <button
        type="button"
        onClick={() => reset()}
        className="mt-6 rounded-full border border-black px-6 py-2 text-lg font-thin text-black dark:border-white dark:text-white"
      >
        Try again
      </button>
    </ErrorScreen>
  );
};

export default Error;
